{
	"title": "Characters",
	"description": "A live conversation instead of a summary. A character from IEC Hero explains the topic from your materials — with links to them.",
	"intro": {
		"title": "Work through a topic with a character",
		"description": "Characters live in IEC Hero. StudyHub feeds fragments of your materials into the conversation so answers rest on them, not on general knowledge.",
		"bullets": {
			"materials": "The character answers from the session materials, not from thin air.",
			"citations": "Every claim is marked with its source — you can open the page.",
			"hero": "Creating and tuning characters stays in Hero."
		},
		"readDocument": "Read the document"
	},
	"blockers": {
		"age_not_confirmed": {
			"title": "Age is not confirmed",
			"description": "This module is available from age {minAge}. Your age comes from your IEC ID profile — add your date of birth there and come back.",
			"action": "Open IEC ID profile"
		},
		"under_minimum": {
			"title": "Not available yet",
			"description": "This module is available from age {minAge}."
		},
		"consent_required": {
			"title": "Consent to data sharing is required",
			"description": "Material fragments and messages will go to IEC Hero — a third-party service.",
			"action": "I agree"
		},
		"consent_outdated": {
			"title": "The document has changed",
			"description": "Data sharing terms have been updated — please confirm the new version.",
			"action": "Accept the new version"
		}
	},
	"actions": {
		"menu": "Section actions",
		"unlink": "Unlink account from IEC Hero",
		"unlinkConfirmTitle": "Unlink account from IEC Hero?",
		"unlinkConfirmDescription": "StudyHub will stop sharing material fragments and messages with IEC Hero. Your sessions stay in the list, but you cannot continue them until you link the accounts again. Linking happens automatically when you start a new session.",
		"unlinkConfirm": "Unlink"
	},
	"unlinked": {
		"title": "The link with Hero is broken",
		"description": "Sessions stay here, but chat with characters needs a link to IEC Hero again. Start any character below — StudyHub will reconnect the accounts.",
		"relink": "Link account again",
		"relinked": "Accounts linked again — you can continue with characters."
	},
	"scenario": {
		"label": "Format",
		"exam-prep": "Exam prep",
		"exam-prepHint": "Short questions one at a time, with a correction right after each answer.",
		"socratic": "Socratic dialogue",
		"socraticHint": "The character leads with questions instead of handing over the answer.",
		"material-explain": "Walk through the material",
		"material-explainHint": "Step-by-step explanation with a check that each step landed.",
		"free-kb": "Free conversation",
		"free-kbHint": "Answers to your questions from the session materials, without a scenario."
	},
	"catalog": {
		"start": "Start a session",
		"starting": "Creating…",
		"empty": "No matching characters found.",
		"stale": "IEC Hero is unavailable — showing a saved list. The data may be out of date.",
		"unavailable": "IEC Hero is unavailable right now. Existing sessions open; new ones cannot be created yet.",
		"emptyFiltered": "Nothing matches this request. Change the search or clear the filters.",
		"loadMore": "Show more",
		"chats": "{count, plural, one {{formatted} chat} other {{formatted} chats}}",
		"chips": {
			"all": "All"
		},
		"filters": {
			"tags": "Tags",
			"tagsSelected": "Tags: {count}",
			"sort": "Sort"
		},
		"sorts": {
			"popular": "Popular",
			"new": "New",
			"name": "By name"
		}
	},
	"sessions": {
		"title": "My sessions",
		"empty": "No sessions yet.",
		"emptyTitle": "No conversations yet",
		"messages": "{count, plural, one {# message} other {# messages}}",
		"materialsMore": "{name} +{count}",
		"materialsCount": "{count, plural, one {# material} other {# materials}}",
		"open": "Open",
		"delete": "Delete",
		"deleted": "Session deleted",
		"deleteTitle": "Delete this session?",
		"deleteDescription": "The session metadata disappears from StudyHub. The chat itself stays in IEC Hero — that is where it lives."
	},
	"chat": {
		"back": "Back",
		"notFound": "Session not found.",
		"openInHero": "Open in Hero",
		"placeholder": "Ask about the material…",
		"send": "Send",
		"stop": "Stop",
		"thinking": "Thinking…",
		"greetingLoading": "Character is greeting you…",
		"voiceRecord": "Record voice message",
		"voiceStop": "Stop recording",
		"voiceCancel": "Cancel recording",
		"voiceSend": "Send voice message",
		"voiceRecording": "Recording…",
		"voiceEncoding": "Preparing voice message…",
		"voiceMessage": "Voice message",
		"voiceFailed": "Could not record voice message.",
		"voiceTooShort": "Hold a bit longer — the recording is too short.",
		"voiceUnsupported": "Voice recording is not supported on this device.",
		"voicePermissionDenied": "Microphone access was denied.",
		"offline": "Messages are stored in IEC Hero — reading and replying require a connection.",
		"historyUnavailable": "History did not load: IEC Hero is unavailable.",
		"moderated": "The answer was cut short: the content did not pass StudyHub's check.",
		"refunded": "The message was not counted — try again.",
		"notRefunded": "The answer had started arriving, so the message was counted.",
		"errors": {
			"generic": "Could not get an answer.",
			"unavailable": "IEC Hero is not responding.",
			"payment_required": "The limit on the IEC Hero side has been reached.",
			"not_linked": "The link to your Hero account was lost — link it again.",
			"rate_limited": "Too often. Please wait a moment.",
			"network": "The connection dropped.",
			"invalid_request": "IEC Hero could not process this voice message. Try again or send text."
		}
	},
	"about": {
		"title": "About the character",
		"noDescription": "No description yet.",
		"oceanTitle": "Personality (OCEAN)",
		"avatarLightbox": "Photo of {name}",
		"ocean": {
			"openness": "Openness",
			"conscientiousness": "Conscientiousness",
			"extraversion": "Extraversion",
			"agreeableness": "Agreeableness",
			"neuroticism": "Neuroticism"
		}
	},
	"tts": {
		"speak": "Speak",
		"stop": "Stop",
		"empty": "Nothing to speak",
		"failed": "Could not speak this reply",
		"quota": "Speech quota exceeded",
		"mediaTitle": "Character reply",
		"playbackRate": "Playback speed",
		"autoplayOn": "Auto-voice on",
		"autoplayOff": "Auto-voice off"
	},
	"showcase": {
		"scenarios": {
			"title": "How you want to work through it",
			"lead": "The scenario sets how the character leads the conversation. You can change it before the next session."
		},
		"characters": {
			"title": "Who you want to work with",
			"lead": "Selected IEC Hero characters. Each has its own character and way of explaining.",
			"leadTopic": "Selected IEC Hero characters. The conversation will follow the materials of the open topic."
		},
		"dialogHint": "Pick a format and a companion — the conversation opens on the current topic.",
		"dialogHintCharactersOnly": "Pick a companion — the conversation opens on the current topic.",
		"searchPlaceholder": "Search the whole catalog"
	},
	"scope": {
		"hint": "By default the character answers from your whole library. Pick materials to narrow the conversation down to them.",
		"chip": {
			"selected": "Context: {count, plural, one {# material} other {# materials}}",
			"all": "All materials"
		},
		"dialog": {
			"title": "Materials for the session",
			"description": "The character answers from these materials, and their fragments are sent to IEC Hero. Picking nothing is fine too — then your whole library is in play."
		}
	},
	"launch": {
		"back": "Back to the list",
		"startingWith": "Starting a conversation with {name}"
	},
	"tabs": {
		"label": "Characters section",
		"catalog": "Catalog",
		"sessions": "My conversations"
	},
	"continue": {
		"title": "Continue",
		"seeAll": "All conversations"
	},
	"scenarioTiles": {
		"title": "Scenarios",
		"lead": "Ready-made conversation openers. Updated daily."
	},
	"preview": {
		"start": "Start a conversation",
		"openHero": "Go to IEC Hero"
	},
	"start": {
		"title": "Start a conversation with {name}",
		"lead": "The format and materials apply to this conversation only.",
		"formatTitle": "Format",
		"materialsTitle": "Materials",
		"topicMaterials": "The conversation follows the materials of the open topic — StudyHub adds them for you.",
		"outbound": {
			"title": "What goes to IEC Hero",
			"description": "Fragments of your materials and your messages are sent out — that is what answering from your own source requires. With no materials picked, a fragment may come from any of them. Whole files, notes and profile data are never sent."
		},
		"existing": {
			"title": "You already have this conversation",
			"continue": "Continue the previous one"
		},
		"confirm": "Start a conversation",
		"confirmNew": "Start a new one",
		"cancel": "Cancel"
	},
	"sectionIntro": {
		"title": "How a character differs from the tutor",
		"body": "The tutor follows a program; a character is a free conversation in a role. Such talks do not count towards a course topic.",
		"bullets": [
			"Scenarios: exam prep, topic breakdown, Socratic dialogue",
			"The talks live in IEC Hero; we keep only the list of them",
			"The list opens offline, the talk itself needs a connection"
		]
	}
}
